import { FlowStateController } from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import { gsap } from "gsap";

import { EventsManager, GameEvents, ReelsEvents } from "../../../abstracts/events/EventsManager";
import { AbstractGameMode } from "../../../abstracts/flow/AbstractGameMode";
import { LevelName, TimelineLabels } from "../../../abstracts/types/enums";
import { IWinLevel } from "../../../abstracts/types/interfaces";
import { winLevels } from "../../UCConfig";
import { UgliestCatch } from "../../UgliestCatch";
import { UCReelsView } from "../../modules/Reels/UCReelsView";
import { totalWinDecorator } from "../decorators/UCTotalWinDecorators";
import { UCNormalWinPresentation } from "../normal/UCNormalWinPresentation";

export let alreadyOpenedReels: { count: number } = { count: 3 };

export class UCFreespinWinPresentation extends UCNormalWinPresentation {
    protected reelsView: UCReelsView;

    constructor(id: string = `UCFreespinWinPresentation`) {
        super(id);
    }

    protected override shouldFinishPresentation(): boolean {
        return this.model.gameData.accumulatedRoundWin <= 0 && this.model.data.freespinsLeft > 0;
    }

    protected override finishPresentation() {
        super.finishPresentation();
    }

    public override start(flowStateController: FlowStateController) {
        super.start(flowStateController);
    }

    protected override handleWinnings() {
        this.flowPresentationTimeline = gsap.timeline({});

        const winSymbols = UgliestCatch.model.finalWinningPosition;
        const isSpinWinBelowStake = UgliestCatch.model.isWinBelowStake(false);
        const isAccumulatedWinBelowStake = UgliestCatch.model.isWinBelowStake(true);

        if (this.model.gameData.totalSpinWinnings <= 0) {
            this.addSpecialAnimations(isSpinWinBelowStake, isAccumulatedWinBelowStake);

            return;
        } else {
            this.noWinCounter = 0;

            let winValue = this.model.gameData.totalSpinWinnings;
            if (this.model.gameData.brokeTheBank) {
                // if (
                //     this.model.gameData.accumulatedRoundWin > this.model.gameData.totalSpinWinnings
                // ) {
                winValue =
                    this.model.gameData.accumulatedRoundWin > this.model.gameData.totalSpinWinnings
                        ? this.model.gameData.accumulatedRoundWin
                        : this.model.gameData.totalSpinWinnings;
                // } else {
                //     winValue = this.model.gameData.totalSpinWinnings;
                // }
            }

            totalWinDecorator({
                winLevels: winLevels,
                winAmount: winValue,
                timeline: this.flowPresentationTimeline,
                winScreen: this.winView.totalWinScreen,
                betValue: this.model.gameData.playedBetValue,
                flowStateController: this.controller,
                winIsBelowStake: isAccumulatedWinBelowStake,
                reels: this.ucReels,
                winSymbolsPos: winSymbols,
                keypadPlugin: this.keypad,
                betWayWins: this.model.gameData.betWayWins,
                currentGameMode: this.model.data.mode,
                onDecoratorComplete: () => {
                    this.addSpecialAnimations(isSpinWinBelowStake, isAccumulatedWinBelowStake);
                },
                finishSpineAnimationDuration: 5,
            });

            this.flowPresentationTimeline.call(
                () => {
                    const win = isAccumulatedWinBelowStake
                        ? -1
                        : this.model.gameData.accumulatedRoundWin;

                    this.keypad.setWin(
                        win,
                        !isAccumulatedWinBelowStake,
                        isAccumulatedWinBelowStake,
                        isAccumulatedWinBelowStake
                    );
                },
                [],
                TimelineLabels.TEXT_COUNT_COMPLETE
            );

            if (this.model.gameData.brokeTheBank) {
                this.flowPresentationTimeline.call(
                    () => {
                        this.keypad.setWin(-1, true, true, true);
                        this.keypad.setZeroBetSpinCounter(-1);
                        this.hideFastSpinButton();
                    },
                    undefined,
                    `${LevelName.MAX_CAP}`
                );
            }

            this.flowPresentationTimeline.resume();
        }
    }

    protected override addSpecialAnimations(
        isWinBelowStake: boolean = false,
        isAccumulatedWinBelow: boolean = false
    ) {
        let outroTimeline: gsap.core.Timeline = gsap.timeline({});

        const addOutroSkip = () => {
            const skipToNextLevel = () => {
                const nextLabel: string = outroTimeline.nextLabel();

                outroTimeline.seek(outroTimeline.labels[nextLabel], false);
            };

            this.controller?.setSkipCallback(skipToNextLevel);
        };

        switch (this.model.gameData.nextMode) {
            case AbstractGameMode.NORMAL: {
                this.keypad.setZeroBetSpinCounter(-1);

                let winValue = this.model.gameData.totalSpinWinnings;
                if (this.model.gameData.brokeTheBank) {
                    winValue =
                        this.model.gameData.accumulatedRoundWin >
                        this.model.gameData.totalSpinWinnings
                            ? this.model.gameData.accumulatedRoundWin
                            : this.model.gameData.totalSpinWinnings;
                }
                let winMultiplier: number = winValue / this.model.gameData.playedBetValue;

                const winLevel: IWinLevel = winLevels.filter(
                    (level: IWinLevel) => level.min <= winMultiplier && level.max > winMultiplier
                )[0];

                if (winLevel.name !== LevelName.MAX_CAP) {
                    this.keypad.setWin(-1);

                    addOutroSkip();

                    outroTimeline = this.winView.showFreespinOutroScreen(
                        this.model.gameData.accumulatedRoundWin,
                        this.onOutroScreenComplete.bind(this, isAccumulatedWinBelow),
                        this.model.data.mode === AbstractGameMode.SUPER_FREESPIN,
                        winLevel,
                        !isAccumulatedWinBelow,
                        this.controller
                    );
                } else {
                    this.onOutroScreenComplete(isAccumulatedWinBelow);
                }

                break;
            }
            default: {
                const totalIsBelowStake: boolean = !isAccumulatedWinBelow ? false : isWinBelowStake;

                this.keypad.setWin(
                    this.model.gameData.accumulatedRoundWin,
                    !totalIsBelowStake,
                    totalIsBelowStake
                );

                this.initializeTimeline();

                const serverOpenedReelsCount =
                    UgliestCatch.model.gameData.extraPositionsOpen.filter(
                        (isOpen) => isOpen
                    ).length;

                if (alreadyOpenedReels.count === 3 && serverOpenedReelsCount === 4) {
                    alreadyOpenedReels.count = 4;

                    const hawkSpinsAnimation = this.winView.showHawkSpinsScreen(
                        this.model.gameData.freespinsLeft
                    );

                    this.flowPresentationTimeline.addLabel(`hawkSpins`);
                    this.flowPresentationTimeline.call(
                        () => {
                            this.controller.setSkipCallback(undefined);
                            this.controller.setSkipCallback(() => {
                                hawkSpinsAnimation.timeScale(3);
                            });
                        },
                        undefined,
                        `hawkSpins-=0.1`
                    );
                    this.flowPresentationTimeline.add(hawkSpinsAnimation, `hawkSpins`);
                    this.flowPresentationTimeline.resume();
                } else if (
                    this.model.data.totalSpinWinnings === 0 &&
                    !UgliestCatch.model.hasFishesToCollect &&
                    UgliestCatch.model.gameData.addedNumberOfFreespinsThisSpin === 0
                ) {
                    this.handleNoWin();
                } else {
                    this.finishPresentation();
                }

                break;
            }
        }
    }

    protected onOutroScreenComplete(isWinBelowStake: boolean) {
        this.keypad.setWin(this.model.gameData.accumulatedRoundWin, true, isWinBelowStake);
        this.controller?.setSkipCallback(undefined);
        this.showFastSpinButton();
        this.finishPresentation();

        EventsManager.trigger(GameEvents.ON_FREE_BETS_END);
    }

    protected handleEvents(): void {
        super.handleEvents();

        EventsManager.on(ReelsEvents.GET_REELS_VIEW, (reelsView: UCReelsView) => {
            this.reelsView = reelsView;
        });
    }
}
